import { lazy, useState, Suspense } from 'react';
import { InclusivelyHidden } from '@uds/inclusively-hidden';
import { generate } from 'shortid';
import { cx } from 'linaria';
import { isNumeric, warning } from '@ux/js';
import './glyph.stylesheet_12gy9fm.css';
import './svgglyph.stylesheet_xamtxn.css';
import { jsx, jsxs } from 'react/jsx-runtime';

const toCssUnit = x => isNumeric(x) ? `${x}px` : x;
const defaultStyles = "d10huf0";
const widthSetStyle = "wuvisba";
const heightSetStyle = "h1hm6gxu";
const getDynamicVars$1 = _ref => {
  let {
    size,
    height,
    width
  } = _ref;
  if (size != null && size > 0) {
    return {
      '--glyph-height': toCssUnit(size),
      '--glyph-width': toCssUnit(size)
    };
  }
  const hasHeight = height != null && height > 0;
  const hasWidth = width != null && width > 0;
  if (hasHeight || hasWidth) {
    return {
      '--glyph-height': hasWidth && toCssUnit(height),
      '--glyph-width': hasHeight && toCssUnit(width)
    };
  }
  return {};
};

const root = {
  svgWrapper: cx("s5uc0pl", defaultStyles),
  heightSet: heightSetStyle,
  widthSet: widthSetStyle
};
const getClassNames = props => {
  const widthSet = props.width != null && props.height == null;
  const heightSet = props.height != null && props.width == null;
  return {
    svgWrapper: cx(root.svgWrapper, props.className, widthSet && root.widthSet, heightSet && root.heightSet)
  };
};
const getDynamicVars = _ref => {
  let {
    size,
    height,
    width,
    color
  } = _ref;
  return {
    ...getDynamicVars$1({
      size,
      height,
      width
    }),
    ...(color != null ? {
      '--glyph-color': color
    } : '')
  };
};

const componentMap = {
  addFile: /*#__PURE__*/lazy(() => import('./AddFile-58611269.js')),
  addFolder: /*#__PURE__*/lazy(() => import('./AddFolder-e34c770e.js')),
  advert: /*#__PURE__*/lazy(() => import('./Advert-daf583d3.js')),
  amazon: /*#__PURE__*/lazy(() => import('./Amazon-c56d615e.js')),
  americanExpress: /*#__PURE__*/lazy(() => import('./AmericanExpress-6122f91a.js')),
  archlinux: /*#__PURE__*/lazy(() => import('./Archlinux-5e4c1d5f.js')),
  arrowBaseDown: /*#__PURE__*/lazy(() => import('./ArrowBaseDown-ac2680e5.js')),
  arrowBaseUpDown: /*#__PURE__*/lazy(() => import('./ArrowBaseUpDown-82b49351.js')),
  arrowBaseUp: /*#__PURE__*/lazy(() => import('./ArrowBaseUp-57d23fd9.js')),
  arrowCircleIn: /*#__PURE__*/lazy(() => import('./ArrowCircleIn-b84f4555.js')),
  arrowCircleOut: /*#__PURE__*/lazy(() => import('./ArrowCircleOut-a8866f3d.js')),
  arrowDown: /*#__PURE__*/lazy(() => import('./ArrowDown-bfceae9a.js')),
  arrowLeft: /*#__PURE__*/lazy(() => import('./ArrowLeft-b3d1bf80.js')),
  arrowRight: /*#__PURE__*/lazy(() => import('./ArrowRight-e619f6c9.js')),
  arrowUpDown: /*#__PURE__*/lazy(() => import('./ArrowUpDown-4f1abc77.js')),
  arrowUp: /*#__PURE__*/lazy(() => import('./ArrowUp-d76bc6e9.js')),
  avatar: /*#__PURE__*/lazy(() => import('./Avatar-a6c73643.js')),
  bag: /*#__PURE__*/lazy(() => import('./Bag-c1be2623.js')),
  basket: /*#__PURE__*/lazy(() => import('./Basket-4d19900e.js')),
  bell: /*#__PURE__*/lazy(() => import('./Bell-8441938f.js')),
  bin: /*#__PURE__*/lazy(() => import('./Bin-f6639aac.js')),
  boxArrowUpRight: /*#__PURE__*/lazy(() => import('./BoxArrowUpRight-961d6100.js')),
  boxInfo: /*#__PURE__*/lazy(() => import('./BoxInfo-9d6c746d.js')),
  calendar: /*#__PURE__*/lazy(() => import('./Calendar-c9301dc8.js')),
  call: /*#__PURE__*/lazy(() => import('./Call-73961d55.js')),
  camera: /*#__PURE__*/lazy(() => import('./Camera-aaea4c89.js')),
  cart: /*#__PURE__*/lazy(() => import('./Cart-44317ccf.js')),
  centos: /*#__PURE__*/lazy(() => import('./Centos-13722c60.js')),
  checkedBox: /*#__PURE__*/lazy(() => import('./CheckedBox-036fd61e.js')),
  chevronDown: /*#__PURE__*/lazy(() => import('./ChevronDown-4831bc40.js')),
  chevronLeft: /*#__PURE__*/lazy(() => import('./ChevronLeft-7fe6b344.js')),
  chevronRight: /*#__PURE__*/lazy(() => import('./ChevronRight-c2a9bc75.js')),
  chevronUp: /*#__PURE__*/lazy(() => import('./ChevronUp-507c7070.js')),
  circleCross: /*#__PURE__*/lazy(() => import('./CircleCross-cb4cac14.js')),
  circleExclamation: /*#__PURE__*/lazy(() => import('./CircleExclamation-78e99dc1.js')),
  circleInfo: /*#__PURE__*/lazy(() => import('./CircleInfo-70a7cfaf.js')),
  circleNegative: /*#__PURE__*/lazy(() => import('./CircleNegative-488bfdb4.js')),
  circlePositive: /*#__PURE__*/lazy(() => import('./CirclePositive-12fd4fd3.js')),
  circleTick: /*#__PURE__*/lazy(() => import('./CircleTick-59ebbda4.js')),
  circle: /*#__PURE__*/lazy(() => import('./Circle-5b9c7a75.js')),
  cirrus: /*#__PURE__*/lazy(() => import('./Cirrus-f075dc27.js')),
  clipboard: /*#__PURE__*/lazy(() => import('./Clipboard-1786c545.js')),
  clock: /*#__PURE__*/lazy(() => import('./Clock-4a90551e.js')),
  cloud: /*#__PURE__*/lazy(() => import('./Cloud-48bb9139.js')),
  code: /*#__PURE__*/lazy(() => import('./Code-8ffc652f.js')),
  cog: /*#__PURE__*/lazy(() => import('./Cog-9be1b793.js')),
  compass: /*#__PURE__*/lazy(() => import('./Compass-e58be479.js')),
  controls: /*#__PURE__*/lazy(() => import('./Controls-d73a1b74.js')),
  coreos: /*#__PURE__*/lazy(() => import('./Coreos-7e405547.js')),
  cpanel: /*#__PURE__*/lazy(() => import('./Cpanel-b0d485b8.js')),
  cpu: /*#__PURE__*/lazy(() => import('./Cpu-e7fcaddc.js')),
  creditCard: /*#__PURE__*/lazy(() => import('./CreditCard-e06c37c1.js')),
  cross: /*#__PURE__*/lazy(() => import('./Cross-0eabba00.js')),
  cycle: /*#__PURE__*/lazy(() => import('./Cycle-a87c75ef.js')),
  database: /*#__PURE__*/lazy(() => import('./Database-fc152691.js')),
  debian: /*#__PURE__*/lazy(() => import('./Debian-3d6a32a9.js')),
  dinersClubInternational: /*#__PURE__*/lazy(() => import('./DinersClubInternational-89cd9709.js')),
  directDebit: /*#__PURE__*/lazy(() => import('./DirectDebit-05bccffc.js')),
  disallowed: /*#__PURE__*/lazy(() => import('./Disallowed-4695eeaf.js')),
  discover: /*#__PURE__*/lazy(() => import('./Discover-fd4cc4f7.js')),
  document: /*#__PURE__*/lazy(() => import('./Document-5ce95d32.js')),
  download: /*#__PURE__*/lazy(() => import('./Download-74efd179.js')),
  drag: /*#__PURE__*/lazy(() => import('./Drag-6b5f922f.js')),
  duplicate: /*#__PURE__*/lazy(() => import('./Duplicate-949a2240.js')),
  ebay: /*#__PURE__*/lazy(() => import('./Ebay-8deb68b5.js')),
  envelope: /*#__PURE__*/lazy(() => import('./Envelope-c675b2eb.js')),
  eway: /*#__PURE__*/lazy(() => import('./Eway-40aa0b15.js')),
  eyeStrikethrough: /*#__PURE__*/lazy(() => import('./EyeStrikethrough-feb29a96.js')),
  eye: /*#__PURE__*/lazy(() => import('./Eye-fcf8a820.js')),
  firewall: /*#__PURE__*/lazy(() => import('./Firewall-0974f5c4.js')),
  folderAdd: /*#__PURE__*/lazy(() => import('./FolderAdd-8e7f68a3.js')),
  folder: /*#__PURE__*/lazy(() => import('./Folder-3299a5b2.js')),
  freebsd: /*#__PURE__*/lazy(() => import('./Freebsd-baa08973.js')),
  ftp: /*#__PURE__*/lazy(() => import('./Ftp-2c4d60bc.js')),
  gauge: /*#__PURE__*/lazy(() => import('./Gauge-d9679235.js')),
  globe: /*#__PURE__*/lazy(() => import('./Globe-2368be41.js')),
  googleWallet: /*#__PURE__*/lazy(() => import('./GoogleWallet-6cadf774.js')),
  gpu: /*#__PURE__*/lazy(() => import('./Gpu-0a9401f8.js')),
  graph: /*#__PURE__*/lazy(() => import('./Graph-dc6187bc.js')),
  grid: /*#__PURE__*/lazy(() => import('./Grid-ff8df99a.js')),
  hamburger: /*#__PURE__*/lazy(() => import('./Hamburger-8cb2f7aa.js')),
  hdd: /*#__PURE__*/lazy(() => import('./Hdd-85ac3222.js')),
  heart: /*#__PURE__*/lazy(() => import('./Heart-a296c98b.js')),
  home: /*#__PURE__*/lazy(() => import('./Home-70f17baa.js')),
  insetNegative: /*#__PURE__*/lazy(() => import('./InsetNegative-6b9e81b7.js')),
  invoice: /*#__PURE__*/lazy(() => import('./Invoice-bc650198.js')),
  jcb: /*#__PURE__*/lazy(() => import('./Jcb-dc64f558.js')),
  key: /*#__PURE__*/lazy(() => import('./Key-88c71283.js')),
  laptop: /*#__PURE__*/lazy(() => import('./Laptop-7433bdd8.js')),
  linux: /*#__PURE__*/lazy(() => import('./Linux-de922628.js')),
  list: /*#__PURE__*/lazy(() => import('./List-2028f362.js')),
  maestro: /*#__PURE__*/lazy(() => import('./Maestro-a691050b.js')),
  magnifyingGlass: /*#__PURE__*/lazy(() => import('./MagnifyingGlass-8eb526dc.js')),
  mastercard: /*#__PURE__*/lazy(() => import('./Mastercard-05fd656b.js')),
  mobile: /*#__PURE__*/lazy(() => import('./Mobile-6ee0b2f5.js')),
  move: /*#__PURE__*/lazy(() => import('./Move-2987955d.js')),
  nodes: /*#__PURE__*/lazy(() => import('./Nodes-8b2d2732.js')),
  opensuse: /*#__PURE__*/lazy(() => import('./Opensuse-9a8d5694.js')),
  padlock: /*#__PURE__*/lazy(() => import('./Padlock-ee41ef44.js')),
  paymentMethodAmericanExpress: /*#__PURE__*/lazy(() => import('./PaymentMethodAmericanExpress-d3191b44.js')),
  paymentMethodDirectDebit: /*#__PURE__*/lazy(() => import('./PaymentMethodDirectDebit-3d5b8f5b.js')),
  paymentMethodMastercard: /*#__PURE__*/lazy(() => import('./PaymentMethodMastercard-8ecbe470.js')),
  paymentMethodPaypal: /*#__PURE__*/lazy(() => import('./PaymentMethodPaypal-2f4ae8eb.js')),
  paymentMethodSepa: /*#__PURE__*/lazy(() => import('./PaymentMethodSepa-13045dcf.js')),
  paymentMethodVisa: /*#__PURE__*/lazy(() => import('./PaymentMethodVisa-c4cf08ca.js')),
  paypal: /*#__PURE__*/lazy(() => import('./Paypal-863db19a.js')),
  pencil: /*#__PURE__*/lazy(() => import('./Pencil-9721b5fb.js')),
  pin: /*#__PURE__*/lazy(() => import('./Pin-840ecbc0.js')),
  playVideo: /*#__PURE__*/lazy(() => import('./PlayVideo-a5fa4027.js')),
  play: /*#__PURE__*/lazy(() => import('./Play-1e8700c0.js')),
  plesk: /*#__PURE__*/lazy(() => import('./Plesk-4ae72fa9.js')),
  plus: /*#__PURE__*/lazy(() => import('./Plus-4c0c1036.js')),
  power: /*#__PURE__*/lazy(() => import('./Power-f88c3178.js')),
  projectBpt: /*#__PURE__*/lazy(() => import('./ProjectBpt-407dc1ef.js')),
  projectSox: /*#__PURE__*/lazy(() => import('./ProjectSox-27447148.js')),
  ram: /*#__PURE__*/lazy(() => import('./Ram-079523b7.js')),
  rosette: /*#__PURE__*/lazy(() => import('./Rosette-16cbf470.js')),
  sage: /*#__PURE__*/lazy(() => import('./Sage-81d2f6e7.js')),
  servers: /*#__PURE__*/lazy(() => import('./Servers-a852bcbb.js')),
  sharedHosting: /*#__PURE__*/lazy(() => import('./SharedHosting-ae4ca19a.js')),
  shieldTick: /*#__PURE__*/lazy(() => import('./ShieldTick-6b4cc78f.js')),
  shield: /*#__PURE__*/lazy(() => import('./Shield-58abb0c8.js')),
  shopify: /*#__PURE__*/lazy(() => import('./Shopify-5b99c156.js')),
  sitemap: /*#__PURE__*/lazy(() => import('./Sitemap-56fbd222.js')),
  skrillMoneybookers: /*#__PURE__*/lazy(() => import('./SkrillMoneybookers-28198a5d.js')),
  skrill: /*#__PURE__*/lazy(() => import('./Skrill-103bc8b0.js')),
  smallCross: /*#__PURE__*/lazy(() => import('./SmallCross-205b5040.js')),
  smallTick: /*#__PURE__*/lazy(() => import('./SmallTick-55c514c1.js')),
  smiley: /*#__PURE__*/lazy(() => import('./Smiley-a2046e48.js')),
  socialFacebook: /*#__PURE__*/lazy(() => import('./SocialFacebook-8b3e6a73.js')),
  socialGooglePlus: /*#__PURE__*/lazy(() => import('./SocialGooglePlus-0e67fc0f.js')),
  socialInstagram: /*#__PURE__*/lazy(() => import('./SocialInstagram-54e27c1d.js')),
  socialLinkedin: /*#__PURE__*/lazy(() => import('./SocialLinkedin-0590c549.js')),
  socialPinterest: /*#__PURE__*/lazy(() => import('./SocialPinterest-4b465474.js')),
  socialRss: /*#__PURE__*/lazy(() => import('./SocialRss-6527eff4.js')),
  socialTwitter: /*#__PURE__*/lazy(() => import('./SocialTwitter-d55fffe5.js')),
  socialVimeo: /*#__PURE__*/lazy(() => import('./SocialVimeo-34b1b57e.js')),
  socialYoutube: /*#__PURE__*/lazy(() => import('./SocialYoutube-571e9d2a.js')),
  solo: /*#__PURE__*/lazy(() => import('./Solo-b5f81d1b.js')),
  spanner: /*#__PURE__*/lazy(() => import('./Spanner-96edd927.js')),
  speechBubble: /*#__PURE__*/lazy(() => import('./SpeechBubble-b5d4bf2e.js')),
  star: /*#__PURE__*/lazy(() => import('./Star-526e9d50.js')),
  stop: /*#__PURE__*/lazy(() => import('./Stop-19cd1dd7.js')),
  switch: /*#__PURE__*/lazy(() => import('./Switch-fd6f2270.js')),
  tablet: /*#__PURE__*/lazy(() => import('./Tablet-0939c306.js')),
  tag: /*#__PURE__*/lazy(() => import('./Tag-a3b6148f.js')),
  textCarat: /*#__PURE__*/lazy(() => import('./TextCarat-5b3b6aa1.js')),
  thumbUp: /*#__PURE__*/lazy(() => import('./ThumbUp-388ef18e.js')),
  tick: /*#__PURE__*/lazy(() => import('./Tick-dfc7a779.js')),
  ticket: /*#__PURE__*/lazy(() => import('./Ticket-a1e0672e.js')),
  transfer: /*#__PURE__*/lazy(() => import('./Transfer-98e8309e.js')),
  triangleExclamation: /*#__PURE__*/lazy(() => import('./TriangleExclamation-4b172be1.js')),
  trigram: /*#__PURE__*/lazy(() => import('./Trigram-5c474db2.js')),
  trophy: /*#__PURE__*/lazy(() => import('./Trophy-28ff9559.js')),
  tv: /*#__PURE__*/lazy(() => import('./Tv-01c69cb4.js')),
  ubuntu: /*#__PURE__*/lazy(() => import('./Ubuntu-1a26e8c7.js')),
  upLevel: /*#__PURE__*/lazy(() => import('./UpLevel-28e5bdac.js')),
  upload: /*#__PURE__*/lazy(() => import('./Upload-fd0ee111.js')),
  visaElectron: /*#__PURE__*/lazy(() => import('./VisaElectron-77203611.js')),
  visa: /*#__PURE__*/lazy(() => import('./Visa-7e64283e.js')),
  westernUnion: /*#__PURE__*/lazy(() => import('./WesternUnion-fa562274.js')),
  windows: /*#__PURE__*/lazy(() => import('./Windows-706b44dc.js')),
  wordpress: /*#__PURE__*/lazy(() => import('./Wordpress-1b229737.js')),
  worldpay: /*#__PURE__*/lazy(() => import('./Worldpay-1d3bb615.js')),
  www: /*#__PURE__*/lazy(() => import('./Www-76972717.js'))
};
function SvgLoader(_ref) {
  let {
    name
  } = _ref;
  const Component = componentMap[name];
  if (Component == null) {
    warning(false, 'missing component with name: %s', name);
    return null;
  }
  return /*#__PURE__*/jsx(Component, {});
}

const SvgGlyph = _ref => {
  let {
    name,
    alt,
    ...rest
  } = _ref;
  const [id] = useState(generate());
  const classNames = getClassNames(rest);
  return /*#__PURE__*/jsx(Suspense, {
    fallback: null,
    children: /*#__PURE__*/jsxs("div", {
      role: "img",
      "aria-labelledby": id,
      className: classNames.svgWrapper,
      style: getDynamicVars(rest),
      children: [/*#__PURE__*/jsx(SvgLoader, {
        name: name
      }), alt != null && /*#__PURE__*/jsx(InclusivelyHidden, {
        id: id,
        children: alt
      })]
    })
  });
};
SvgGlyph.displayName = 'SvgGlyph';
var SvgGlyph$1 = SvgGlyph;

let Palette;
(function (Palette) {
  Palette["GREY_01"] = "grey01";
  Palette["GREY_02"] = "grey02";
  Palette["GREY_03"] = "grey03";
  Palette["GREY_04"] = "grey04";
  Palette["GREY_05"] = "grey05";
  Palette["GREY_06"] = "grey06";
  Palette["GREY_07"] = "grey07";
  Palette["GREY_08"] = "grey08";
  Palette["GREY_09"] = "grey09";
  Palette["GREY_10"] = "grey10";
  Palette["GREY_11"] = "grey11";
  Palette["GREY_12"] = "grey12";
  Palette["RED_01"] = "red01";
  Palette["RED_02"] = "red02";
  Palette["YELLOW_01"] = "yellow01";
  Palette["YELLOW_02"] = "yellow02";
  Palette["GREEN_01"] = "green01";
  Palette["GREEN_02"] = "green02";
  Palette["HEADER_BACKGROUND"] = "headerBackground";
  Palette["HEADER_FOREGROUND"] = "headerForeground";
  Palette["HEADER_HIGHLIGHT"] = "headerHighlight";
  Palette["PRIMARY"] = "primary";
  Palette["PRIMARY_CONTRAST"] = "primaryContrast";
  Palette["PRIMARY_GRADIENT"] = "primaryGradient";
  Palette["SECONDARY"] = "secondary";
  Palette["TERTIARY"] = "tertiary";
  Palette["HIGHLIGHT"] = "highlight";
  Palette["HIGHLIGHT_CONTRAST"] = "highlightContrast";
  Palette["CTA"] = "cta";
  Palette["CTA_CONTRAST"] = "ctaContrast";
})(Palette || (Palette = {}));

export { SvgGlyph$1 as Glyph, Palette, SvgGlyph$1 as default };
